<template>
    <!--
        Milo: the still pose at once (it is also the fallback), the 3D figure over it once
        three.js and the model have arrived. The canvas fills this box; the caller sizes it.
    -->
    <div class="relative overflow-hidden" :aria-label="label" role="img">
        <img
            v-show="!live"
            :src="miloPose(state, false)"
            alt=""
            class="absolute inset-0 size-full object-contain"
            draggable="false"
        >
        <canvas v-if="wants3d" ref="canvas" class="absolute inset-0 size-full" :class="live ? 'opacity-100' : 'opacity-0'" />
    </div>
</template>

<script setup lang="ts">
    /**
     * Milo's figure for a phone or a desk (libs/milo/milo.ts holds the shared rules).
     *
     * The 3D figure loads only while this component is mounted and only where WebGL 2 is there;
     * anything that fails on the way (the chunk, the model, the context, a GPU that draws fewer
     * than 15 frames a second) leaves the still pose standing. Pixel ratio at most 2, the eyes
     * follow the finger or the mouse, and the loop pauses while the page is hidden or the
     * figure is scrolled out of sight.
     */
    import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue'
    import { useI18n } from 'vue-i18n'

    import { canRender3d, createMilo, miloPose, type IMiloScene, type MiloState } from '@/libs/milo/milo'

    const props = withDefaults(defineProps<{
        state?:   MiloState
        /** Head and shoulders (a small avatar) or the whole figure. */
        framing?: 'full' | 'head'
        /** False draws only the still pose. */
        enable3d?: boolean
    }>(), {
        state:    'idle',
        framing:  'head',
        enable3d: true,
    })

    const { t } = useI18n()

    const label = computed((): string => t('milo.figure'))

    const canvas = useTemplateRef<HTMLCanvasElement>('canvas')
    const wants3d = ref(props.enable3d && canRender3d(false))
    const live = ref(false)

    let scene: IMiloScene | null = null
    let visible = true
    let observer: IntersectionObserver | null = null
    let gone = false

    const run = () => {
        if (!scene) return

        if (visible && !document.hidden) scene.start()
        else scene.stop()
    }

    const giveUp = () => {
        scene?.dispose()
        scene = null
        live.value = false
        wants3d.value = false
    }

    onMounted(async () => {
        if (!wants3d.value || !canvas.value) return

        try {
            const made = await createMilo(canvas.value, {
                maxPixelRatio: 2,
                antialias:     true,
                fps:           0,
                lowPower:      false,
                framing:       props.framing,
                trackPointer:  true,
                minFps:        15,
                onFail:        giveUp,
            })

            if (gone) {
                made.dispose()

                return
            }

            scene = made
            scene.setState(props.state)
            run()
            // Shown once a frame has been drawn: a paused page keeps the still pose.
            requestAnimationFrame(() => requestAnimationFrame(() => { if (scene) live.value = true }))
        } catch {
            giveUp()

            return
        }

        if (typeof IntersectionObserver !== 'undefined' && canvas.value) {
            observer = new IntersectionObserver((entries) => {
                visible = entries.some((entry) => entry.isIntersecting)
                run()
            })
            observer.observe(canvas.value)
        }

        document.addEventListener('visibilitychange', run)
    })

    onBeforeUnmount(() => {
        gone = true
        observer?.disconnect()
        document.removeEventListener('visibilitychange', run)
        scene?.dispose()
        scene = null
    })

    watch(() => props.state, (state) => scene?.setState(state))
</script>
